import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import { formatDate } from '../../utils';
import { showError, showInfo } from '../../actions/notificationActions';
import approvals from '../../services/approvals.service';

import Loading from '../Loading';
import ApprovalStatusBadge from '../ApprovalStatusBadge';
import ViewResourceLink from '../ViewResourceLink';
import DataTable from '../DataTable';

const OTP_TTL_SECONDS = 600; // ~10 minutes

const safeDate = (d) => (d ? formatDate(d) : '—');

const prettyJson = (value) => {
  if (value === null || value === undefined) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const formatCountdown = (seconds) => {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? '0' : ''}${r}`;
};

class ApprovalDetail extends Component {
  state = {
    loading: false,
    notFound: false,
    request: null,
    lastResult: null, // { result, status_code } from a successful approval

    // OTP / approve
    otpModalOpen: false,
    otpSent: false,
    otpSending: false,
    otpSubmitting: false,
    otp: '',
    maskedTo: '',
    otpSecondsLeft: 0,
    approving: false,

    // decline
    declineModalOpen: false,
    reason: '',
    declining: false,
  };

  get reference() {
    return this.props.match.params.reference;
  }

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }

  load = ({ silent } = {}) => {
    if (!silent) this.setState({ loading: true });
    approvals
      .get(this.reference)
      .then((res) => {
        this.setState({ loading: false, notFound: false, request: res.data });
      })
      .catch((err) => {
        this.setState({ loading: false });
        if (err && err.status === 'not-found') {
          this.setState({ notFound: true });
        } else {
          this.props.dispatch(showError(err));
        }
      });
  };

  /* ----------------------------- OTP countdown ----------------------------- */

  startCountdown = () => {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.setState({ otpSecondsLeft: OTP_TTL_SECONDS });
    this.countdownTimer = setInterval(() => {
      this.setState((s) => {
        if (s.otpSecondsLeft <= 1) {
          clearInterval(this.countdownTimer);
          return { otpSecondsLeft: 0 };
        }
        return { otpSecondsLeft: s.otpSecondsLeft - 1 };
      });
    }, 1000);
  };

  /* ------------------------------- approve --------------------------------- */

  onApproveClick = () => {
    const { request } = this.state;
    if (!request) return;

    if (request.otp_required) {
      this.setState({
        otpModalOpen: true,
        otp: '',
        otpSent: false,
        maskedTo: '',
      });
      this.sendOtp();
    } else {
      if (!window.confirm('Approve and run this operation now?')) return;
      this.runApproval();
    }
  };

  sendOtp = () => {
    this.setState({ otpSending: true });
    approvals
      .sendOtp(this.reference)
      .then((res) => {
        const to = (res.meta && res.meta.to) || 'your email';
        this.setState({ otpSending: false, otpSent: true, maskedTo: to });
        this.startCountdown();
        this.props.dispatch(showInfo(`Code sent to ${to}`));
      })
      .catch((err) => {
        this.setState({ otpSending: false });
        this.handleActionError(err);
      });
  };

  // Used both for the direct (no-OTP) path and the OTP submit path.
  runApproval = (otp) => {
    const submittingKey = otp ? 'otpSubmitting' : 'approving';
    this.setState({ [submittingKey]: true });

    approvals
      .approve(this.reference, otp)
      .then((res) => {
        this.setState({
          [submittingKey]: false,
          otpModalOpen: false,
          otp: '',
          request: res.data || this.state.request,
          lastResult: res.meta || null,
        });
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.props.dispatch(
          showInfo(res.message || 'Approved — operation completed')
        );
        this.load({ silent: true });
      })
      .catch((err) => {
        this.setState({ [submittingKey]: false });
        this.handleActionError(err);
      });
  };

  submitOtp = () => {
    const { otp } = this.state;
    if (!/^\d{6}$/.test(otp)) {
      this.props.dispatch(
        showError('Enter the 6-digit code sent to your email.')
      );
      return;
    }
    this.runApproval(otp);
  };

  /* ------------------------------- decline --------------------------------- */

  submitDecline = () => {
    const reason = (this.state.reason || '').trim();
    if (!reason) {
      this.props.dispatch(
        showError('A reason is required to decline this request.')
      );
      return;
    }
    this.setState({ declining: true });
    approvals
      .decline(this.reference, reason)
      .then((res) => {
        this.setState({
          declining: false,
          declineModalOpen: false,
          reason: '',
          request: res.data || this.state.request,
        });
        this.props.dispatch(showInfo(res.message || 'Request declined'));
        this.load({ silent: true });
      })
      .catch((err) => {
        this.setState({ declining: false });
        this.handleActionError(err);
      });
  };

  // Shared error handling. If someone else already actioned the request
  // (optimistic-concurrency 422), refresh to reflect the true state.
  handleActionError = (err) => {
    const message = (err && err.message) || '';
    if (/no longer pending/i.test(message)) {
      this.props.dispatch(showError(message));
      this.setState({ otpModalOpen: false, declineModalOpen: false });
      this.load({ silent: true });
      return;
    }
    this.props.dispatch(showError(err));
  };

  closeOtpModal = () => {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.setState({ otpModalOpen: false, otp: '' });
  };

  render() {
    const { loading, notFound, request, lastResult } = this.state;

    if (loading && !request) {
      return (
        <Col className="content">
          <Loading />
        </Col>
      );
    }

    if (notFound) {
      return (
        <Col className="content">
          <Card>
            <CardBody className="text-center my-4">
              <h5>Approval request not found</h5>
              <Link to="/approvals" className="btn btn-primary mt-3">
                Back to Approvals
              </Link>
            </CardBody>
          </Card>
        </Col>
      );
    }

    if (!request) return null;

    const op = request.operation || {};
    const canAct = request.can_check && request.status === 'pending';

    return (
      <Col className="content">
        <Row>
          <Col xs={12}>
            <Card>
              <CardHeader className="ml-2 d-flex justify-content-between align-items-center">
                <div>
                  <CardTitle>{request.title || 'Approval request'}</CardTitle>
                  <p className="category">
                    <code>{request.action}</code>
                  </p>
                </div>
                <div>
                  <ApprovalStatusBadge status={request.status} />{' '}
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => this.load()}
                  >
                    Refresh
                  </Button>
                </div>
              </CardHeader>

              <CardBody style={{ padding: 0 }}>
                <div className="mb-3 d-flex align-items-center">
                  <Link
                    to="/approvals"
                    className="btn btn-sm btn-outline-primary mr-2"
                  >
                    ← Back to Approvals
                  </Link>
                  <ViewResourceLink
                    uri={request.resource_uri}
                    className="btn btn-sm btn-info"
                  />
                </div>

                <DataTable card={false} minWidth={800} loading={loading}>
                  <tbody>
                    <tr>
                      <td className="text-muted">Maker</td>
                      <td>{request.maker ? request.maker.name : '—'}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Checker</td>
                      <td>{request.checker ? request.checker.name : '—'}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Created</td>
                      <td>{safeDate(request.created_at)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Expires</td>
                      <td>{safeDate(request.expires_at)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Executed</td>
                      <td>{safeDate(request.executed_at)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">OTP required</td>
                      <td>{request.otp_required ? 'Yes' : 'No'}</td>
                    </tr>
                  </tbody>
                </DataTable>

                <h6 className="mt-3">Captured operation</h6>
                <DataTable card={false} minWidth={800}>
                  <tbody>
                    <tr>
                      <td className="text-muted">Method &amp; path</td>
                      <td>
                        <code>
                          {(op.method || '').toUpperCase()} {op.path || ''}
                        </code>
                      </td>
                    </tr>
                  </tbody>
                </DataTable>
                <Row>
                  <Col md={6}>
                    <small className="text-muted">Body</small>
                    <pre
                      className="border rounded p-2"
                      style={{ maxHeight: 240, overflow: 'auto' }}
                    >
                      {prettyJson(op.body)}
                    </pre>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted">Params</small>
                    <pre
                      className="border rounded p-2"
                      style={{ maxHeight: 240, overflow: 'auto' }}
                    >
                      {prettyJson(op.params)}
                    </pre>
                  </Col>
                </Row>

                {request.status === 'denied' && (
                  <div className="alert alert-danger mt-3">
                    <strong>Declined.</strong>{' '}
                    {request.denial_reason || 'No reason provided.'}
                  </div>
                )}

                {(request.result || lastResult) && (
                  <div className="mt-3">
                    <h6>
                      Operation outcome
                      {lastResult && lastResult.status_code
                        ? ` (HTTP ${lastResult.status_code})`
                        : ''}
                    </h6>
                    <pre
                      className="border rounded p-2"
                      style={{ maxHeight: 280, overflow: 'auto' }}
                    >
                      {prettyJson(
                        (lastResult && lastResult.result) || request.result
                      )}
                    </pre>
                  </div>
                )}

                {/* Four-eyes: only show controls the server will accept. */}
                {canAct ? (
                  <div className="mt-4">
                    <Button
                      color="success"
                      onClick={this.onApproveClick}
                      disabled={this.state.approving}
                    >
                      {this.state.approving ? 'Approving…' : 'Approve'}
                    </Button>{' '}
                    <Button
                      color="danger"
                      onClick={() => this.setState({ declineModalOpen: true })}
                    >
                      Decline
                    </Button>
                  </div>
                ) : (
                  request.status === 'pending' && (
                    <p className="text-muted mt-4">
                      You cannot action this request (a checker must be a
                      different authorised admin).
                    </p>
                  )
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ------------------------------ OTP modal ----------------------------- */}
        <Modal isOpen={this.state.otpModalOpen} toggle={this.closeOtpModal}>
          <ModalHeader toggle={this.closeOtpModal}>
            Confirm approval with OTP
          </ModalHeader>
          <ModalBody>
            {this.state.otpSent ? (
              <p>
                A 6-digit code was sent to{' '}
                <strong>{this.state.maskedTo}</strong>.
                {this.state.otpSecondsLeft > 0 ? (
                  <span>
                    {' '}
                    It expires in{' '}
                    <strong>
                      {formatCountdown(this.state.otpSecondsLeft)}
                    </strong>
                    .
                  </span>
                ) : (
                  <span className="text-danger">
                    {' '}
                    The code has expired — resend a new one.
                  </span>
                )}
              </p>
            ) : (
              <p>Sending a code to your email…</p>
            )}

            <FormGroup>
              <Label for="approval-otp">One-time code</Label>
              <Input
                id="approval-otp"
                type="tel"
                inputMode="numeric"
                maxLength={6}
                placeholder="6-digit code"
                value={this.state.otp}
                onChange={(e) =>
                  this.setState({
                    otp: e.target.value.replace(/\D/g, '').slice(0, 6),
                  })
                }
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="link"
              disabled={this.state.otpSending}
              onClick={this.sendOtp}
            >
              {this.state.otpSending ? 'Resending…' : 'Resend code'}
            </Button>
            <Button color="secondary" onClick={this.closeOtpModal}>
              Cancel
            </Button>
            <Button
              color="success"
              onClick={this.submitOtp}
              disabled={this.state.otpSubmitting || this.state.otp.length !== 6}
            >
              {this.state.otpSubmitting ? 'Approving…' : 'Approve'}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ---------------------------- Decline modal --------------------------- */}
        <Modal
          isOpen={this.state.declineModalOpen}
          toggle={() => this.setState({ declineModalOpen: false })}
        >
          <ModalHeader
            toggle={() => this.setState({ declineModalOpen: false })}
          >
            Decline request
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="decline-reason">Reason (required)</Label>
              <Input
                id="decline-reason"
                type="textarea"
                rows={3}
                placeholder="Why is this being declined?"
                value={this.state.reason}
                onChange={(e) => this.setState({ reason: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => this.setState({ declineModalOpen: false })}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={this.submitDecline}
              disabled={this.state.declining || !this.state.reason.trim()}
            >
              {this.state.declining ? 'Declining…' : 'Decline'}
            </Button>
          </ModalFooter>
        </Modal>
      </Col>
    );
  }
}

export default connect()(ApprovalDetail);
